import {
  createSelector,
  createFeatureSelector,
} from '@ngrx/store';
import * as fromSite from './site.reducer';
import { Site } from '../../models/site';
import { SiteStatus } from '../../enums/site';
import { Batch } from '../../models/batch';
import { selectBatchEntities } from '../batch/batch.selectors';

// get the selectors
const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = fromSite.adapter.getSelectors();

export const selectSiteState = createFeatureSelector<fromSite.State>('sites');

export const selectSiteIds = createSelector(
  selectSiteState,
  (state): string[] => selectIds(state).map(String)
);
export const selectSiteEntities = createSelector(
  selectSiteState,
  selectEntities
);
export const selectAllSites = createSelector(
  selectSiteState,
  selectAll
);
export const selectSiteTotal = createSelector(
  selectSiteState,
  selectTotal
);

export const selectCurrentSiteId = createSelector(
  selectSiteState,
  fromSite.getSelectedSiteId
);

export const selectCurrentSite = createSelector(
  selectSiteEntities,
  selectCurrentSiteId,
  (siteEntities, siteId) => (siteId) ? siteEntities[siteId] : null
);

export const selectSearchSiteText = createSelector(
  selectSiteState,
  fromSite.getSearchSiteText
);

export const selectSiteDensityHighlight = createSelector(
  selectSiteState,
  fromSite.getSiteDensityHighlight
);

export const selectSiteById = (id: string) => createSelector(
  selectSiteEntities,
  (entities) => {
    return entities[id];
  }
);


export const selectAdvancedFeaturesAvailable = createSelector(
  selectCurrentSite,
  (site: Site | null | undefined) =>
    !!site && site.status === SiteStatus.RUNNING
);

export const selectAdvancedFeaturesActivating = createSelector(
  selectCurrentSite,
  (site: Site | null | undefined) =>
    !!site &&
    [
      SiteStatus.INSTANCE_CREATING,
      SiteStatus.INSTANCE_CREATED,
      SiteStatus.INSTANCE_CONFIGURING,
      SiteStatus.INSTANCE_CONFIGURED,
      SiteStatus.SERVICE_STARTING,
      SiteStatus.SERVICE_CONFIGURED,
      SiteStatus.INITIALIZING,
    ].includes(site.status)
);

export const selectAdvancedFeaturesAvailableBySiteId = (siteId: string) =>
  createSelector(
    selectSiteById(siteId),
    (site: Site | undefined) =>
      !!site && site.status === SiteStatus.RUNNING
  );

export const selectAdvancedFeaturesActivatingBySiteId = (siteId: string) =>
  createSelector(
    selectSiteById(siteId),
    (site: Site | undefined) =>
      !!site &&
      [
        SiteStatus.INSTANCE_CREATING,
        SiteStatus.INSTANCE_CREATED,
        SiteStatus.INSTANCE_CONFIGURING,
        SiteStatus.INSTANCE_CONFIGURED,
        SiteStatus.SERVICE_STARTING,
        SiteStatus.SERVICE_CONFIGURED,
        SiteStatus.INITIALIZING,
      ].includes(site.status)
  );

export const selectAdvancedFeaturesExpirationDate = createSelector(
  selectCurrentSite,
  (obj) => {
    if (!obj?.structureStopEta) {
      return null;
    }

    return obj.structureStopEta;
  }
);

export const selectSiteUserSettings = createSelector(
  selectSiteState,
  state => state.userSettings
);

export const selectSiteUserSettingsLoading = createSelector(
  selectSiteState,
  state => state.userSettingsLoading
);

export const selectSiteUserSettingsUpdating = createSelector(
  selectSiteState,
  state => state.userSettingsUpdating
);

export const selectCurrentSiteBatch = createSelector(
  selectCurrentSite,
  selectBatchEntities,
  (
    site,
    batchEntities
  ): Batch | null => {
    if (!site?.lastBatch) {
      return null;
    }

    return batchEntities[site.lastBatch] ?? null;
  }
);

export const selectCurrentSiteBatchDate = createSelector(
  selectCurrentSiteBatch,
  (batch): number | null =>
    batch?.dateAcquired ?? null
);
